import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from '../modals/confirmation';
import { toast } from '../../utils/toast';
import { useQuery } from 'react-query';
import { createDocumentShareLink, emailDocument } from '../../services/documents';
import { getCompanyMessaging } from '../../services/company';
import { buildViberInvoiceUrl, buildWhatsAppInvoiceUrl } from '../../utils/invoiceMessaging';
import { documentLabel } from '../../constants/labels';

const LINK_PLACEHOLDER = '{link}';

// Fill the company template's placeholders. {link} is only known once the
// secure share link is prepared, so it is substituted at send time.
export const renderDocumentMessage = (template, { clientName, documentNumber, jobNumber }) =>
    String(template || '')
        .replace(/\{clientName\}/g, clientName)
        .replace(/\{documentNumber\}/g, documentNumber)
        .replace(/\{jobNumber\}/g, String(jobNumber));

export const withDocumentLink = (message, url, viewLabel) =>
    message.includes(LINK_PLACEHOLDER)
        ? message.replace(new RegExp(LINK_PLACEHOLDER.replace(/[{}]/g, '\\$&'), 'g'), url)
        : `${message.trim()}\n\n${viewLabel}\n${url}`;

/**
 * Send panel for a Pickup Receipt or Work Order — same UX as the invoice
 * panel: recipient email, editable message prefilled from the company
 * template, secure share link, WhatsApp / Viber / Email.
 *
 * Props: { document: { id, type, number, jobId }, client: { firstName, lastName, phone, email }, onClose }
 */
const DocumentSharePanel = ({ document, client, onClose }) => {
    const { t } = useTranslation();
    const [message, setMessage] = useState('');
    const [messageEdited, setMessageEdited] = useState(false);
    const [recipient, setRecipient] = useState('');
    const [shareUrl, setShareUrl] = useState('');
    const [linkError, setLinkError] = useState('');
    const [preparing, setPreparing] = useState(false);
    const [sending, setSending] = useState(false);
    const [confirmSend, setConfirmSend] = useState(false);

    const { data: messaging } = useQuery(['company', 'messaging'], getCompanyMessaging, {
        staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false,
    });
    const settings = messaging?.code === 200 && messaging.message ? messaging.message : null;

    const documentId = document?.id;
    const type = document?.type;
    const label = documentLabel(type).toLowerCase();
    const documentNumber = document?.number || '';
    const jobId = document?.jobId;
    const clientName = [client?.firstName, client?.lastName].filter(Boolean).join(' ') || t('jobs.client');
    const phone = client?.phone || '';
    const hasPhone = !!String(phone).replace(/\D/g, '');
    const template = type === 'WORK_ORDER'
        ? (settings?.workOrderMessageTemplate || t('defaultMessages.workOrder'))
        : (settings?.receiptMessageTemplate || t('defaultMessages.receipt'));

    const prepareShareLink = async () => {
        if (!documentId) return;
        setShareUrl('');
        setLinkError('');
        setPreparing(true);
        try {
            const response = await createDocumentShareLink(documentId);
            if (response?.code !== 200 || !response?.message?.url) {
                throw new Error(typeof response?.message === 'string' ? response.message : t('documentShare.linkCouldNotBePrepared'));
            }
            setShareUrl(response.message.url);
        } catch (error) {
            setLinkError(error?.message || t('documentShare.linkCouldNotBePrepared'));
        } finally {
            setPreparing(false);
        }
    };

    useEffect(() => {
        if (!documentId) return;
        setRecipient(client?.email || '');
        setMessageEdited(false);
        prepareShareLink();
        // A different document needs its own secure link.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentId]);

    useEffect(() => {
        if (!documentId || messageEdited) return;
        setMessage(renderDocumentMessage(template, { clientName, documentNumber, jobNumber: jobId }));
    }, [documentId, template, clientName, documentNumber, jobId, messageEdited]);

    if (!document) return null;

    const shareMessage = (channel) => {
        if (!shareUrl) return;
        const text = withDocumentLink(message, shareUrl, t('defaultMessages.viewDocumentLink', { label }));
        if (channel === 'whatsapp') {
            window.open(buildWhatsAppInvoiceUrl(phone, text), '_blank', 'noopener,noreferrer');
            return;
        }
        window.location.href = buildViberInvoiceUrl(phone, text);
    };

    const sendEmail = async () => {
        setSending(true);
        try {
            const body = message.includes(LINK_PLACEHOLDER) && shareUrl ? withDocumentLink(message, shareUrl, t('defaultMessages.viewDocumentLink', { label })) : message;
            const response = await emailDocument(documentId, { email: recipient, message: body });
            if (response?.code !== 200) throw new Error(response?.message || t('documentShare.emailFailed'));
            toast.success(t('documentShare.sentTo', { label: documentLabel(type), recipient }));
        } catch (error) {
            toast.error(error?.message || t('documentShare.emailCouldNotBeSent', { label }));
        } finally {
            setSending(false);
        }
    };

    return <div className="qap-backdrop" onMouseDown={onClose}>
        <aside className="qap" onMouseDown={(event) => event.stopPropagation()} aria-label={t('documentShare.sendDocument', { label })}>
            <div className="qap-head"><div><b>{t('documentShare.sendDocument', { label })}</b><span>{documentNumber} · {t('jobs.jobNumber', { id: jobId })} · {clientName}</span></div><button type="button" onClick={onClose}>×</button></div>
            <div className="qap-form invoice">
                <label>
                    {t('documentShare.recipientEmail')}
                    <input type="email" value={recipient} onChange={(event) => setRecipient(event.target.value)} placeholder="client@example.com" />
                    {!recipient && <small className="qap-hint">{t('documentShare.noEmailHint')}</small>}
                </label>
                <label>
                    {t('documentShare.message')}
                    <textarea rows="7" value={message} onChange={(event) => { setMessage(event.target.value); setMessageEdited(true); }} />
                    <small className="qap-hint">{t('documentShare.prefillHint', { label })}</small>
                </label>
                {preparing && <div className="qap-share-note neutral">{t('documentShare.preparingLink')}</div>}
                {linkError && <div className="qap-share-note error">{linkError} <button type="button" onClick={prepareShareLink}>{t('documentShare.tryAgain')}</button></div>}
                {!hasPhone && <div className="qap-share-note neutral">{t('documentShare.noPhoneHint')}</div>}
                <div className="qap-send">
                    <button disabled={sending || preparing || !shareUrl} onClick={() => shareMessage('whatsapp')}>WhatsApp</button>
                    <button disabled={sending || preparing || !shareUrl} onClick={() => shareMessage('viber')}>Viber</button>
                    <button disabled={sending || !recipient} onClick={() => setConfirmSend(true)}>{sending ? t('documentShare.sending') : t('common.email')}</button>
                </div>
            </div>
            <ConfirmationModal display={confirmSend} toggle={setConfirmSend} tone="primary"
                title={t('documentShare.sendDocument', { label }) + '?'} confirmText={t('invoiceShare.send')} cancelText={t('common.cancel')}
                message={t('documentShare.willBeEmailedTo', { document: documentNumber || label, recipient })}
                confirmAction={sendEmail} />
        </aside>
    </div>;
};

export default DocumentSharePanel;
